import { Button } from "@workspace/ui/components/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@workspace/ui/components/drawer";
import { VisuallyHidden } from "@workspace/ui/components/visually-hidden";
import { useIsMobile } from "@workspace/ui/hooks/use-mobile";
import { FilterIcon } from "lucide-react";
import { useQueryStates } from "nuqs";
import { useRef } from "react";
import { countActiveMarketplaceFilters } from "./marketplace-active-filters";
import { MarketplaceFilterControls } from "./marketplace-filter-controls";
import { marketplaceSearchParamsParser } from "./marketplace-search-params";

export function MarketplaceFilterControlsDrawer({
  userLatitude,
  userLongitude,
}: {
  userLatitude?: number;
  userLongitude?: number;
}) {
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();
  const [searchParams] = useQueryStates(marketplaceSearchParamsParser);
  const activeFilterCount = countActiveMarketplaceFilters(searchParams);

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <Button
          aria-label={
            activeFilterCount > 0
              ? `Open filters, ${activeFilterCount} active`
              : "Open filters"
          }
          className="relative size-9"
          ref={isMobile ? triggerButtonRef : null}
          size="icon"
          variant={activeFilterCount > 0 ? "default" : "secondary"}
        >
          <FilterIcon className="h-4 w-4" />
          {activeFilterCount > 0 ? (
            <span className="-right-1 -top-1 absolute flex size-4 items-center justify-center rounded-full bg-background font-semibold text-[10px] text-foreground ring-1 ring-border">
              {activeFilterCount}
            </span>
          ) : null}
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[calc(100dvh-4rem)]">
        <VisuallyHidden>
          <DrawerHeader>
            <DrawerTitle>Filters</DrawerTitle>
            <DrawerDescription>
              Adjust your marketplace filters
            </DrawerDescription>
          </DrawerHeader>
        </VisuallyHidden>
        <div className="min-h-0 flex-1 overflow-y-auto px-4">
          <MarketplaceFilterControls
            userLatitude={userLatitude}
            userLongitude={userLongitude}
          />
        </div>
        <DrawerFooter>
          <DrawerClose asChild>
            <Button className="w-full" variant="outline">
              Close
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
